import { useLayoutEffect, useRef } from "react";
import { Square } from "lucide-react";
import { Button } from "../atoms/Button/index.js";
import { Tooltip } from "../molecules/Tooltip/Tooltip.js";

/** Task 14 — the active Run's Stop control, as the host reports it. */
export interface PlayerAIRunControl {
  readonly state: "running" | "stopping";
  /** Whether THIS viewer may stop it; the server re-checks every press. */
  readonly canStop: boolean;
  readonly onStop: () => void;
}

/** Task 14 — the shared pause after a stop, in a group conversation. */
export interface PlayerAIPauseNotice {
  readonly state: "stopping" | "stopped";
  /** Requests collected while Player AI was stopped. */
  readonly pendingCount: number;
  /** Facilitator or owner: may Resume or Discard pending. */
  readonly canDecide: boolean;
  /** A Resume or Discard is in flight. */
  readonly busy?: boolean | undefined;
  readonly onResume: () => void;
  readonly onDiscard: () => void;
}

/**
 * Stop, inside the composer, while a Run is active. "Stopping…" holds until
 * the server confirms; the stopped response then carries its own "Stopped"
 * marker, so this control simply leaves.
 */
export function ComposerStopControl({
  control,
  alongsideSend = false,
}: {
  control: PlayerAIRunControl;
  alongsideSend?: boolean;
}) {
  if (!control.canStop) return null;
  const stopping = control.state === "stopping";
  const label = stopping ? "Stopping response" : "Stop response";
  return (
    <Tooltip content={stopping ? "Stopping…" : "Stop response"}>
      <Button
        type="button"
        variant={alongsideSend ? "secondary" : "primary"}
        iconOnly
        size="sm"
        aria-label={label}
        aria-busy={stopping}
        disabled={stopping}
        onClick={control.onStop}
        className="shrink-0 self-center"
      >
        <Square className="h-3.5 w-3.5" aria-hidden />
        {stopping ? (
          <span role="status" className="sr-only">
            Stopping…
          </span>
        ) : null}
      </Button>
    </Tooltip>
  );
}

/** The look both composer notices share: one quiet row above the input. */
const NOTICE_ROW =
  "rounded-md border border-pb-border bg-pb-card-elevated px-2 py-1 text-dense-1 text-pb-text-secondary";

/**
 * "Player AI stopped · 3 requests waiting", shared by everyone in the
 * conversation, with Resume and Discard pending for the facilitator or owner.
 */
export function ComposerPauseNotice({
  notice,
}: {
  notice: PlayerAIPauseNotice;
}) {
  const waiting =
    notice.pendingCount === 1
      ? "1 request waiting"
      : `${notice.pendingCount} requests waiting`;
  const text =
    notice.state === "stopping"
      ? notice.pendingCount > 0
        ? `Player AI is stopping · ${waiting}`
        : "Player AI is stopping…"
      : notice.pendingCount > 0
        ? `Player AI stopped · ${waiting}`
        : "Player AI stopped";
  const decidable =
    notice.state === "stopped" && notice.pendingCount > 0 && notice.canDecide;
  return (
    <div
      role="status"
      data-composer-pause
      className={`flex min-w-0 flex-wrap items-center gap-2 ${NOTICE_ROW}`}
    >
      <span className="min-w-0 flex-1">{text}</span>
      {decidable ? (
        <span className="flex shrink-0 items-center gap-1.5">
          <Button
            variant="secondary"
            size="xs"
            loading={notice.busy === true}
            onClick={notice.onResume}
          >
            Resume
          </Button>
          <Button
            variant="ghost"
            size="xs"
            disabled={notice.busy === true}
            onClick={notice.onDiscard}
          >
            Discard pending
          </Button>
        </span>
      ) : null}
    </div>
  );
}

/** PB-5447 — a pending approval THIS viewer can decide, as the host words it. */
export interface PlayerAIApprovalNotice {
  /** What is waiting, e.g. "Canvas approval waiting". */
  readonly label: string;
  /** Review's accessible name: says what it opens. */
  readonly reviewLabel: string;
  /** Take the reader to the card: reveal, highlight and focus it. */
  readonly onReview: () => void;
}

/**
 * PB-5447 — "Canvas approval waiting · Review", above the input, for the one
 * person who must decide. In flow, so it never covers the newest message. It
 * carries no live region: the host announces a new approval once.
 */
export function ComposerApprovalNotice({
  notice,
  onFocusLost,
}: {
  notice: PlayerAIApprovalNotice;
  /** Called when the notice leaves while Review holds keyboard focus. */
  onFocusLost: () => void;
}) {
  const reviewRef = useRef<HTMLButtonElement | null>(null);
  const focusLost = useRef(onFocusLost);
  useLayoutEffect(() => {
    focusLost.current = onFocusLost;
  });
  // Someone else decided while a keyboard user sat on Review: hand focus to
  // the composer rather than dropping it to the page.
  useLayoutEffect(
    () => () => {
      if (reviewRef.current && reviewRef.current === document.activeElement) {
        focusLost.current();
      }
    },
    [],
  );
  return (
    <div
      data-composer-approval
      className={`flex min-w-0 items-center gap-2 ${NOTICE_ROW}`}
    >
      <span className="min-w-0 flex-1 truncate" title={notice.label}>
        {notice.label}
      </span>
      {/* Secondary: the composer's one accent is its Send (or Stop), and
          Review only navigates (packages/ui invariant 9). */}
      <Button
        ref={reviewRef}
        variant="secondary"
        size="xs"
        className="shrink-0"
        aria-label={notice.reviewLabel}
        onClick={notice.onReview}
      >
        Review
      </Button>
    </div>
  );
}
